import React, { useCallback, useEffect, useRef } from 'react';
import { FaGoogle, FaGithub, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/auth.css';

const LoginModal = ({ isOpen, onClose }) => {
  const { authErrorKey, clearAuthError, login, loading } = useAuth();
  const { t } = useTranslation();
  const modalRef = useRef(null);
  const errorRef = useRef(null);

  const handleLogin = useCallback((provider) => {
    clearAuthError();
    login(provider);
  }, [clearAuthError, login]);

  const handleClose = useCallback(() => {
    clearAuthError();
    onClose?.();
  }, [clearAuthError, onClose]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }

    const previouslyFocusedElement = document.activeElement;
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const focusTimeout = window.setTimeout(() => {
      const firstFocusable = modalRef.current?.querySelector(focusableSelector);
      firstFocusable?.focus?.();
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !loading) {
        event.preventDefault();
        handleClose();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = Array.from(
        modalRef.current?.querySelectorAll(focusableSelector) || []
      );

      if (focusableElements.length === 0) {
        event.preventDefault();
        modalRef.current?.focus?.();
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimeout);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElement?.focus?.();
    };
  }, [handleClose, isOpen, loading]);

  useEffect(() => {
    if (isOpen && authErrorKey) {
      errorRef.current?.focus?.();
    }
  }, [authErrorKey, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal-content"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
        aria-describedby="login-modal-description"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="login-modal-title">{t('loginModal.title')}</h2>
          <button
            type="button"
            className="close-button"
            onClick={handleClose}
            aria-label={t('loginModal.close')}
          >
            <FaTimes aria-hidden="true" />
          </button>
        </div>
        
        <div className="modal-body">
          <p id="login-modal-description">{t('loginModal.subtitle')}</p>

          {authErrorKey && (
            <div ref={errorRef} className="auth-error-message" role="alert" tabIndex="-1">
              {t(`loginModal.errors.${authErrorKey}`)}
            </div>
          )}
          
          <div className="auth-buttons">
            <button 
              type="button"
              className="auth-button google-button"
              onClick={() => handleLogin('google')}
              disabled={loading}
            >
              <FaGoogle className="auth-icon" />
              {t('loginModal.continueWithGoogle')}
            </button>
            
            <button 
              type="button"
              className="auth-button github-button"
              onClick={() => handleLogin('github')}
              disabled={loading}
            >
              <FaGithub className="auth-icon" />
              {t('loginModal.continueWithGithub')}
            </button>
          </div>
          
          <div className="auth-footer">
            <p>{t('loginModal.terms')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
